import { Box, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import type { ReactNode } from "react";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { useNetworkContext } from "context/NetworkContext";
import DifferenceOutlinedIcon from "@mui/icons-material/DifferenceOutlined";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import { TEXT } from "constants/text";
import {
  getSidebarNavIconSx,
  getSidebarNavItemSx,
  getSidebarPaperSx,
  getSidebarToggleButtonSx,
  getSidebarToggleRowSx,
  sidebarContentSx,
  sidebarListSx,
} from "./sidebarMenuStyles";
import { useShellLayout } from "./ShellLayoutContext";
import { getBreakinDefaultChannels } from "services/breakinMetadata";

const prefetchBreakinUi = () => {
  void import("components/breakinlog/BreakinLogFilters");
  void import("components/breakinlog/BreakinLogTable");
  void import("components/breakinlog/AddBreakinLog");
};

const navigationItems = [
  { to: "/actuals", label: TEXT.SIDEBAR_LABELS.AS_RUNS, icon: <AccountTreeOutlinedIcon /> },
  { to: "/reports", label: TEXT.SIDEBAR_LABELS.REPORTS, icon: <ArticleOutlinedIcon /> },
  { to: "/breakinlog", label: TEXT.SIDEBAR_LABELS.BIL, icon: <FormatListBulletedOutlinedIcon /> },
  { to: "/compare-tool", label: TEXT.SIDEBAR_LABELS.COMPARE_TOOL, icon: <DifferenceOutlinedIcon /> },
  { to: "/soundmouse", label: TEXT.SIDEBAR_LABELS.SOUNDMOUSE, icon: <GraphicEqRoundedIcon /> },
];

const navigationAliases: Record<string, string[]> = {
  "/actuals": ["/"],
};

const isNavItemSelected = (pathname: string, to: string) =>
  navigationAliases[to]?.includes(pathname) || Boolean(matchPath({ path: `${to}/*`, end: false }, pathname));

function SidebarMenu() {
  const { defaultCh, setDefaultCh, userRole } = useNetworkContext();
  const { isSidebarExpanded, toggleSidebar } = useShellLayout();
  const location = useLocation();
  const navigate = useNavigate();
  const hideUI = !userRole || userRole === "no-access" || userRole === "new-user";

  const prefetchBreakin = () => {
    prefetchBreakinUi();

    if (defaultCh.length === 0) {
      void getBreakinDefaultChannels()
        .then(setDefaultCh)
        .catch((error) => console.error("Error prefetching Breakin Log channels", error));
    }
  };

  if (hideUI) {
    return null;
  }

  const handleNavigate = (to: string) => {
    if (to === "/breakinlog") {
      prefetchBreakin();
    }
    navigate(to);
  };

  const renderNavItem = (to: string, label: string, icon: ReactNode) => {
    const isSelected = isNavItemSelected(location.pathname, to);

    return (
      <Tooltip
        key={to}
        title={label}
        placement="right"
        disableHoverListener={isSidebarExpanded}
        disableFocusListener={isSidebarExpanded}
        disableTouchListener={isSidebarExpanded}
      >
        <ListItemButton
          aria-label={isSidebarExpanded ? undefined : label}
          selected={isSelected}
          sx={(theme) => getSidebarNavItemSx(theme, isSidebarExpanded)}
          onClick={() => handleNavigate(to)}
          onMouseEnter={to === "/breakinlog" ? prefetchBreakin : undefined}
          onFocus={to === "/breakinlog" ? prefetchBreakin : undefined}
        >
          <ListItemIcon sx={getSidebarNavIconSx(isSidebarExpanded)}>{icon}</ListItemIcon>
          {isSidebarExpanded && (
            <ListItemText
              primary={label}
              primaryTypographyProps={{
                fontFamily: '"TT Hoves Pro", sans-serif',
                fontSize: "1rem",
                lineHeight: "1.5rem",
                fontWeight: isSelected ? 600 : 400,
                letterSpacing: "0.015em",
                noWrap: true,
              }}
            />
          )}
        </ListItemButton>
      </Tooltip>
    );
  };

  return (
    <Drawer
      variant="permanent"
      PaperProps={{
        sx: (theme) => getSidebarPaperSx(theme, isSidebarExpanded),
      }}
    >
      <Box sx={sidebarContentSx}>
        <Box sx={getSidebarToggleRowSx(isSidebarExpanded)}>
          <Tooltip title={isSidebarExpanded ? "Collapse navigation" : "Expand navigation"}>
            <IconButton
              aria-label={isSidebarExpanded ? "Collapse navigation" : "Expand navigation"}
              onClick={toggleSidebar}
              size="small"
              sx={(theme) => getSidebarToggleButtonSx(theme)}
            >
              {isSidebarExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        <List sx={sidebarListSx}>
          {navigationItems.map((item) => renderNavItem(item.to, item.label, item.icon))}
          {userRole === "admin" &&
            renderNavItem("/admin", TEXT.SIDEBAR_LABELS.ADMIN, <AdminPanelSettingsOutlinedIcon />)}
        </List>
      </Box>
    </Drawer>
  );
}

export default SidebarMenu;
